import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';
import VueApexCharts from 'vue-apexcharts';

export default {
    name: 'Financial',
    mixins: [HelperMixin],
    components: {
        PaginationHelper,
        apexchart: VueApexCharts,
    },

    async created() {
        await this.CheckLoginStatus();
        if (!this.loginDetails || !this.canViewFinancialUi()) {
            this.$router.replace('/Home');
            return;
        }

        await Promise.all([
            this.GetAllBranches(),
            this.loadFacilityDepartments(),
        ]);

        this.initReportDateRange();
        this.loadFinancialReports();
        this.GetInfo(1);
    },

    computed: {
        totalPages() {
            return Math.ceil(this.pages / this.pageSize);
        },
        monthlyRows() {
            return (this.reportSummary && this.reportSummary.monthly) || [];
        },
        breakdownRows() {
            return (this.reportSummary && this.reportSummary.breakdown) || [];
        },
        topInboundProducts() {
            return (this.reportSummary && this.reportSummary.topInboundProducts) || [];
        },
        topOutboundProducts() {
            return (this.reportSummary && this.reportSummary.topOutboundProducts) || [];
        },
        isFacilityScoped() {
            return !!(this.loginDetails && this.isFacilityCenterRole() && this.loginDetails.branchesId);
        },
        reportTitle() {
            return (this.reportSummary && this.reportSummary.title) || 'البيانات المالية';
        },
        summaryCards() {
            return (this.reportSummary && this.reportSummary.cards) || [];
        },
        financialScopeText() {
            if (this.reportSummary && this.reportSummary.description) {
                return this.reportSummary.description;
            }
            return this.isFacilityScoped
                ? 'يعرض ما استلمه المرفق من الوزارة وما صرفه إلى الأقسام التابعة له.'
                : 'يعرض قيمة الصرف للمرافق الصحية والحركات المالية المرتبطة بها.';
        },
        categoryOptions() {
            if (this.isFacilityScoped) {
                return [
                    { id: 'received_from_ministry', name: 'استلام من الوزارة' },
                    { id: 'issued_to_departments', name: 'صرف إلى الأقسام' },
                    { id: 'returned_to_ministry', name: 'إرجاع إلى الوزارة' },
                    { id: 'damage', name: 'تالف / إعدام' },
                ];
            }
            return [
                { id: 'facility_issue', name: 'صرف للمرافق' },
                { id: 'direct_dispatch', name: 'صرف مباشر' },
                { id: 'facility_returns', name: 'مرتجع من المرافق' },
            ];
        },
        counterpartyLabel() {
            return this.isFacilityScoped ? 'القسم' : 'المرفق';
        },
        counterpartyOptions() {
            return this.isFacilityScoped ? (this.FacilityDepartmentsList || []) : (this.Branches || []);
        },
        counterpartyPlaceholder() {
            return `فلترة حسب ${this.counterpartyLabel}`;
        },
        hasLedgerFilters() {
            return !!((this.Search && this.Search.trim()) || this.filterCategory || this.filterCounterpartyId);
        },
        branchCoverageCount() {
            return (this.reportSummary && this.reportSummary.breakdown && this.reportSummary.breakdown.length) || 0;
        },
        stockCoverageCount() {
            return (this.stockRows && this.stockRows.length) || 0;
        },
        monthlyInboundLabel() {
            return (this.reportSummary && this.reportSummary.seriesLabels && this.reportSummary.seriesLabels.inbound) || 'وارد';
        },
        monthlyOutboundLabel() {
            return (this.reportSummary && this.reportSummary.seriesLabels && this.reportSummary.seriesLabels.outbound) || 'منصرف';
        },
        breakdownTitle() {
            return (this.reportSummary && this.reportSummary.breakdownTitle) || 'التفصيل';
        },
        topInboundTitle() {
            return (this.reportSummary && this.reportSummary.topInboundTitle) || 'أعلى الأصناف الواردة';
        },
        topOutboundTitle() {
            return (this.reportSummary && this.reportSummary.topOutboundTitle) || 'أعلى الأصناف المنصرفة';
        },
        breakdownTabLabel() {
            return this.isFacilityScoped ? 'الأقسام' : 'المرافق';
        },
        monthlyAverageInbound() {
            if (!this.monthlyRows.length) {
                return 0;
            }

            return this.totalMonthlyInbound / this.monthlyRows.length;
        },
        monthlyAverageOutbound() {
            if (!this.monthlyRows.length) {
                return 0;
            }

            return this.totalMonthlyOutbound / this.monthlyRows.length;
        },
        totalMonthlyInbound() {
            return this.monthlyRows.reduce((sum, row) => sum + (Number(row.inboundValue) || 0), 0);
        },
        totalMonthlyOutbound() {
            return this.monthlyRows.reduce((sum, row) => sum + (Number(row.outboundValue) || 0), 0);
        },
        peakMonthRow() {
            if (!this.monthlyRows.length) {
                return null;
            }

            return this.monthlyRows.reduce((maxRow, row) => {
                const currentTotal = (Number(row.inboundValue) || 0) + (Number(row.outboundValue) || 0);
                const maxTotal = (Number(maxRow.inboundValue) || 0) + (Number(maxRow.outboundValue) || 0);
                return currentTotal > maxTotal ? row : maxRow;
            }, this.monthlyRows[0]);
        },
        mostActiveEntity() {
            if (!this.breakdownRows.length) {
                return null;
            }

            return this.breakdownRows.reduce((maxRow, row) => {
                const currentTotal = (Number(row.inboundValue) || 0) + (Number(row.outboundValue) || 0);
                const maxTotal = (Number(maxRow.inboundValue) || 0) + (Number(maxRow.outboundValue) || 0);
                return currentTotal > maxTotal ? row : maxRow;
            }, this.breakdownRows[0]);
        },
        stockAverageValue() {
            if (!this.stockRows.length) {
                return 0;
            }

            return this.stockGrandTotal / this.stockRows.length;
        },
        topStockRow() {
            return this.stockRows.length ? this.stockRows[0] : null;
        },
        stockRowsWithShare() {
            if (!this.stockRows.length || !this.stockGrandTotal) {
                return this.stockRows.map((row) => ({ ...row, share: 0 }));
            }

            return this.stockRows.map((row) => ({
                ...row,
                share: ((Number(row.totalValue) || 0) / this.stockGrandTotal) * 100,
            }));
        },
        productComparisonRows() {
            const rowsMap = new Map();
            const register = (rows, key) => {
                rows.forEach((row) => {
                    const id = row.productId || row.productName;
                    if (!rowsMap.has(id)) {
                        rowsMap.set(id, {
                            productId: row.productId,
                            productName: row.productName || '—',
                            inboundValue: 0,
                            outboundValue: 0,
                        });
                    }

                    rowsMap.get(id)[key] = Number(row.totalValue) || 0;
                });
            };

            register(this.topInboundProducts, 'inboundValue');
            register(this.topOutboundProducts, 'outboundValue');

            return Array.from(rowsMap.values())
                .map((row) => ({
                    ...row,
                    totalValue: row.inboundValue + row.outboundValue,
                    netValue: row.inboundValue - row.outboundValue,
                }))
                .sort((a, b) => b.totalValue - a.totalValue);
        },
        quickInsightCards() {
            return [
                {
                    key: 'avg-inbound',
                    label: `متوسط ${this.monthlyInboundLabel} الشهري`,
                    value: this.monthlyAverageInbound,
                    suffix: 'د.ل',
                },
                {
                    key: 'avg-outbound',
                    label: `متوسط ${this.monthlyOutboundLabel} الشهري`,
                    value: this.monthlyAverageOutbound,
                    suffix: 'د.ل',
                },
                {
                    key: 'top-entity',
                    label: this.isFacilityScoped ? 'أكثر قسم نشاطا' : 'أكثر مرفق نشاطا',
                    text: this.mostActiveEntity ? this.mostActiveEntity.entityName : 'لا توجد بيانات',
                    subtext: this.mostActiveEntity ? `${this.formatNumber((Number(this.mostActiveEntity.inboundValue) || 0) + (Number(this.mostActiveEntity.outboundValue) || 0))} د.ل` : '',
                },
                {
                    key: 'top-stock',
                    label: 'أعلى مخزن بالقيمة',
                    text: this.topStockRow ? this.topStockRow.stockingName : 'لا توجد بيانات',
                    subtext: this.topStockRow ? `${this.formatNumber(this.topStockRow.totalValue)} د.ل` : '',
                },
            ];
        },
    },

    data() {
        return {
            pageNo: 1,
            pageSize: 10,
            pages: 0,
            state: 0,
            Search: '',
            Info: [],
            FacilityDepartmentsList: [],
            activeFinancialTab: 'overview',
            reportDateRange: null,
            reportSummary: null,
            reportsLoading: false,
            stockRows: [],
            stockGrandTotal: 0,
            monthlyChartSeries: [],
            monthlyChartOptions: {},
            branchChartSeries: [],
            branchChartOptions: {},
            stockDonutSeries: [],
            stockDonutOptions: {},
            filterCategory: '',
            filterCounterpartyId: '',
        };
    },

    methods: {
        async loadFacilityDepartments() {
            if (!this.isFacilityScoped) {
                this.FacilityDepartmentsList = [];
                return;
            }

            try {
                const res = await this.$http.GetAllFacilityDepartments(this.loginDetails.branchesId, true);
                this.FacilityDepartmentsList = res.data.info || [];
            } catch {
                this.FacilityDepartmentsList = [];
            }
        },

        initReportDateRange() {
            const today = new Date();
            const from = new Date(today.getFullYear(), today.getMonth(), 1);
            this.reportDateRange = [this.toYmd(from), this.toYmd(today)];
        },

        toYmd(d) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        },

        onFinancialTabChange(tab) {
            const name = typeof tab === 'string' ? tab : (tab && tab.name);
            if (name === 'payments' && !this.Info.length) {
                this.GetInfo(1);
                return;
            }

            if (name !== 'payments' && !this.reportSummary && !this.reportsLoading) {
                this.loadFinancialReports();
            }
        },

        onCategoryChange() {
            this.filterCounterpartyId = '';
            this.GetInfo(1);
        },

        clearLedgerFilters() {
            this.Search = '';
            this.filterCategory = '';
            this.filterCounterpartyId = '';
            this.GetInfo(1);
        },

        loadFinancialReports() {
            if (!this.reportDateRange || this.reportDateRange.length !== 2) {
                this.initReportDateRange();
            }

            const from = this.reportDateRange[0];
            const to = this.reportDateRange[1];
            this.reportsLoading = true;
            this.$blockUI.Start();

            const p1 = this.$http.GetFinancialReportsSummary(from, to);
            const p2 = this.$http.GetFinancialReportsStockValue();

            Promise.all([p1, p2])
                .then(([sumRes, stockRes]) => {
                    this.reportSummary = sumRes.data;
                    this.applyMonthlyCharts(sumRes.data);
                    this.applyBreakdownCharts(sumRes.data);

                    this.stockRows = stockRes.data.rows || [];
                    this.stockGrandTotal = stockRes.data.grandTotal || 0;
                    this.applyStockDonut(this.stockRows);
                })
                .catch(() => {
                    this.reportSummary = null;
                    this.stockRows = [];
                    this.stockGrandTotal = 0;
                })
                .finally(() => {
                    this.reportsLoading = false;
                    this.$blockUI.Stop();
                });
        },

        applyMonthlyCharts(data) {
            const rows = (data && data.monthly) || [];
            this.monthlyChartSeries = [
                { name: this.monthlyInboundLabel, data: rows.map((x) => Number(x.inboundValue) || 0) },
                { name: this.monthlyOutboundLabel, data: rows.map((x) => Number(x.outboundValue) || 0) },
            ];
            this.monthlyChartOptions = {
                chart: { type: 'bar', toolbar: { show: true }, fontFamily: 'inherit' },
                plotOptions: { bar: { columnWidth: '55%', borderRadius: 4 } },
                dataLabels: { enabled: false },
                stroke: { show: true, width: 2, colors: ['transparent'] },
                xaxis: { categories: rows.map((x) => `${x.year}/${x.month}`) },
                yaxis: { labels: { formatter: (val) => this.formatNumber(val) } },
                colors: ['#7367f0', '#28c76f'],
                legend: { position: 'top', horizontalAlign: 'right' },
                tooltip: { y: { formatter: (val) => `${this.formatNumber(val)} د.ل` } },
            };
        },

        applyBreakdownCharts(data) {
            const rows = (data && data.breakdown) || [];
            this.branchChartSeries = [
                { name: this.monthlyInboundLabel, data: rows.map((x) => Number(x.inboundValue) || 0) },
                { name: this.monthlyOutboundLabel, data: rows.map((x) => Number(x.outboundValue) || 0) },
            ];
            this.branchChartOptions = {
                chart: { type: 'bar', toolbar: { show: true }, fontFamily: 'inherit' },
                plotOptions: { bar: { horizontal: true, barHeight: '70%', borderRadius: 4 } },
                dataLabels: { enabled: false },
                xaxis: { categories: rows.map((x) => x.entityName || '—') },
                yaxis: { labels: { formatter: (val) => this.formatNumber(val) } },
                colors: ['#7367f0', '#28c76f'],
                legend: { position: 'top', horizontalAlign: 'right' },
                tooltip: { y: { formatter: (val) => `${this.formatNumber(val)} د.ل` } },
            };
        },

        applyStockDonut(rows) {
            if (!rows.length) {
                this.stockDonutSeries = [];
                this.stockDonutOptions = {};
                return;
            }

            this.stockDonutSeries = rows.map((x) => Number(x.totalValue) || 0);
            this.stockDonutOptions = {
                labels: rows.map((x) => x.stockingName || '—'),
                chart: { fontFamily: 'inherit' },
                legend: { position: 'bottom' },
                plotOptions: {
                    pie: {
                        donut: {
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    label: 'الإجمالي',
                                    formatter: () => `${this.formatNumber(this.stockGrandTotal)} د.ل`,
                                },
                            },
                        },
                    },
                },
                tooltip: {
                    y: {
                        formatter: (val) => `${this.formatNumber(val)} د.ل`,
                    },
                },
                colors: ['#7367f0', '#00cfe8', '#28c76f', '#ff9f43', '#ea5455', '#808390'],
            };
        },

        GetInfo(pageNo) {
            this.pageNo = pageNo || 1;
            this.$blockUI.Start();

            this.$http.GetFinancial(
                this.pageNo,
                this.pageSize,
                this.Search,
                this.filterCategory,
                this.filterCounterpartyId
            )
                .then((response) => {
                    this.$blockUI.Stop();
                    this.Info = response.data.info || [];
                    this.pages = response.data.count || 0;
                })
                .catch(() => {
                    this.$blockUI.Stop();
                    this.Info = [];
                    this.pages = 0;
                });
        },
    },
};
